import type { AdjacencyList, IDFSVisitor, Vertex, VertexList } from './AdjacencyList';
import { ESearchDirection } from './AdjacencyList';
import { NotDAGError } from './CycleDetection';

export interface VertexWithDependencies {
  vertex: Vertex;
  dependencies?: VertexList;
}

// Topological sort visitors
//
// This visitor merely writes the linear ordering of vertices.
// Note that as soon as push to the back of the list is used,
// the recorded order is the reverse topological order,
// thus must be reversed.
//
class _DFSTopologicalSorter implements IDFSVisitor {
  protected readonly _result: VertexList = [];

  public backEdge(vFrom: Vertex, vTo: Vertex): void {
    throw new NotDAGError(vFrom, vTo);
  }

  public finishVertex(v: Vertex): void {
    this._result.push(v);
  }

  public getResult(): VertexList {
    return this._result.reverse();
  }
}

class _DFSTopologicalSorterInv extends _DFSTopologicalSorter {
  public getResult(): VertexList {
    return this._result;
  }
}

class _DFSTopologicalSorterWithDependencies extends _DFSTopologicalSorter {
  protected readonly _dependencies = new Map<Vertex, VertexList>();

  public examineEdge(vFrom: Vertex, vTo: Vertex): void {
    const dep = this._dependencies.get(vTo);
    if (!dep) {
      this._dependencies.set(vTo, [vFrom]);
    } else {
      dep.push(vFrom);
    }
  }

  public getDependencies(): Map<Vertex, VertexList> {
    return this._dependencies;
  }
}

class _DFSTopologicalSorterWithDependenciesInv extends _DFSTopologicalSorterWithDependencies {
  public examineEdge(vFrom: Vertex, vTo: Vertex): void {
    const dep = this._dependencies.get(vFrom);
    if (!dep) {
      this._dependencies.set(vFrom, [vTo]);
    } else {
      dep.push(vTo);
    }
  }

  public getResult(): VertexList {
    return this._result;
  }
}

export function topologicalSortWithDependencies(
  direction: ESearchDirection,
  adj: AdjacencyList,
  vertexList?: VertexList,
  startVertex?: Vertex
): Array<VertexWithDependencies> {
  if (direction != ESearchDirection.Inputs && direction != ESearchDirection.Outputs) {
    throw new Error('Logic error');
  }
  const vis =
    direction == ESearchDirection.Outputs
      ? new _DFSTopologicalSorterWithDependencies()
      : new _DFSTopologicalSorterWithDependenciesInv();
  adj.depthFirstSearch(direction, vis, vertexList, startVertex);
  const dep = vis.getDependencies();
  return vis.getResult().map((v) => ({ vertex: v, dependencies: dep.get(v) }));
}

export function topologicalSortWithoutDependencies(
  direction: ESearchDirection,
  adj: AdjacencyList,
  vertexList?: VertexList,
  startVertex?: Vertex
): VertexList {
  if (direction != ESearchDirection.Inputs && direction != ESearchDirection.Outputs) {
    throw new Error('Logic error');
  }
  const vis = direction == ESearchDirection.Outputs ? new _DFSTopologicalSorter() : new _DFSTopologicalSorterInv();
  adj.depthFirstSearch(direction, vis, vertexList, startVertex);
  return vis.getResult();
}
